import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/components/ui/cn';
import type { Dimension, ScoringWeights } from '../types';
import { DIM_COLORS, weightedComponents, type TFn } from '../derive';

// Minimum visible width (px) for a segment so very small contributions don't disappear.
const MIN_SEGMENT_PX = 4;

export interface ColorBarProps {
  components: Partial<Record<Dimension, number>>;
  weights: ScoringWeights;
  t: TFn;
  /** Height token — 'md' = 12px (default), 'sm' = 8px for cards, 'xs' = 4px for inline. */
  size?: 'xs' | 'sm' | 'md';
}

const HEIGHT_CLASS: Record<NonNullable<ColorBarProps['size']>, string> = {
  xs: 'h-1',
  sm: 'h-2',
  md: 'h-3',
};

export function ColorBar({ components, weights, t, size = 'md' }: ColorBarProps) {
  const wc = weightedComponents(components, weights).sort(
    (a, b) => b.contribution - a.contribution,
  );
  const total = wc.reduce((s, x) => s + x.contribution, 0);
  const heightClass = HEIGHT_CLASS[size];

  if (total <= 0) {
    return (
      <div
        role="img"
        aria-label={t('quiz.result.scoreBreakdownAria')}
        className={cn(
          'w-full rounded-full border border-ink-100 bg-paper',
          heightClass,
        )}
      />
    );
  }

  const ariaLabel =
    t('quiz.result.scoreBreakdownAria') +
    ': ' +
    wc
      .map(
        (x) =>
          `${t(`quiz.result.dim.${x.dim}`)} ${Math.round(x.component * 100)}`,
      )
      .join(', ');

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={cn(
        'flex w-full overflow-hidden rounded-full border border-ink-100 bg-paper',
        heightClass,
      )}
    >
      {wc.map((x) => {
        const pct = (x.contribution / total) * 100;
        return (
          <Tooltip
            key={x.dim}
            content={t('quiz.result.componentTooltip', {
              dim: t(`quiz.result.dim.${x.dim}`),
              component: Math.round(x.component * 100),
              weight: x.weight.toFixed(1),
            })}
            className="h-full"
            style={{ width: `${pct}%`, minWidth: `${MIN_SEGMENT_PX}px` }}
          >
            {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
            <span
              tabIndex={0}
              data-testid={`color-bar-segment-${x.dim}`}
              className={cn(
                'block h-full w-full transition-all duration-200 focus:outline-none',
                DIM_COLORS[x.dim],
              )}
            />
            {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
          </Tooltip>
        );
      })}
    </div>
  );
}
